import {
  ASButton,
  ASComponentSize,
  ASIconButton,
  ComponentSize,
  GearUserIcon,
  UserSettingsIcon,
} from '@eleventheye/asui';
import { Cls, Emojis } from 'app/constants';
import { useAppSelector } from 'app/hooks';
import { getIsUserInGameDetailsScene, getSelectedScene } from 'app/slices/NavigationSlice';
import { getIsUserLoggedIn } from 'app/slices/UserSlice';
import {
  ShowAdminActionsButton,
  ShowHeadToHeadButton,
  ShowStandingsButton,
  ShowStatsButton,
  ShowWeeklyGamesButton,
  UserAccountButton,
} from 'components/iconbutton/IconButton';
import {
  FinanceIcon,
  FinanceModeIcon,
  FootballIcon,
  HeadToHeadIcon,
  ScheduleIcon,
  StandingsIcon,
  SwordsIcon,
} from 'helpers/AppIconHelpers';
import useViewportDimensions from 'hooks/useViewportDimensions';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { AppRoutes } from 'routes/Routes';
import { AppScene } from 'types';
import styles from './TopNavigation.module.scss';

const titleWeeklyGames = 'Show Weekly Games';
const titleShowStandings = 'Show Standings';
const titleShowStats = 'Show All Stats';
const titleShowLandingPage = 'LP | Disabled for the time being as stats monitoring is going to be refactored';
const titleUserAccount = 'User Account';
const titleLoginRegister = 'Login / Register';

type TopNavigationProps = {
  className?: string;
  isEditor?: boolean;
  setShowLandingPage?: (show: boolean) => void;
  setStandingsActive?: (show: boolean) => void;
  setShowWeeklyGamesPage?: (show: boolean) => void;
  setShowStatsForAllTeams?: (show: boolean) => void;
  setShowCategoryRankings?: (show: boolean) => void;
  setShowComparison?: (show: boolean) => void;
  setShowHeadToHead?: (show: boolean) => void;
  showUserAccountPanel?: VoidFunction;
};

const TopNavigation: React.FC<TopNavigationProps> = ({
  className = '',
  isEditor = false,
  setShowLandingPage = () => undefined,
  setStandingsActive = () => undefined,
  setShowWeeklyGamesPage = () => undefined,
  setShowStatsForAllTeams = () => undefined,
  setShowCategoryRankings = () => undefined,
  setShowComparison = () => undefined,
  setShowHeadToHead = () => undefined,
  showUserAccountPanel = () => undefined,
}) => {
  const baseCls = `${styles.TopNavigationContainer} ${className}`;
  const [btnSize, setBtnSize] = useState<ASComponentSize>(ComponentSize.Medium);
  const isGameDetailsScene = useAppSelector(getIsUserInGameDetailsScene);
  const isLoggedIn = useAppSelector(getIsUserLoggedIn);
  const selectedScene = useAppSelector(getSelectedScene);
  const { isAppMobile } = useViewportDimensions();
  const navigate = useNavigate();

  useEffect(() => {
    setBtnSize(isAppMobile ? ComponentSize.Small : ComponentSize.Medium);
    const isAppMobileStr = isAppMobile ? 'Mobile' : 'Desktop';
    const msg = `${Emojis.eyes} %c TopNavigation %c %c ${isAppMobileStr} %c size %c ${btnSize} %c`;
    console.log(msg, Cls.DBl, '', Cls.Info, '', Cls.Val, '');
  }, [isAppMobile]);

  const isActionButtonsDisabled = useMemo(() => isGameDetailsScene || !isLoggedIn, [isGameDetailsScene, isLoggedIn]);

  const isLoggedInButtonDisabled = selectedScene === AppScene.Authentication;

  return (
    <div className={baseCls}>
      {isEditor && (
        <>
          <ASIconButton
            disabled={isActionButtonsDisabled || true}
            selected={selectedScene === AppScene.Landing}
            icon={FootballIcon}
            strokeWidth={0}
            size={btnSize}
            title={titleShowLandingPage}
            iconSize={btnSize === ComponentSize.Large ? 30 : 22}
            onClick={() => setShowLandingPage(selectedScene !== AppScene.Landing)}
          />

          <ShowAdminActionsButton icon={GearUserIcon} />
        </>
      )}
      <NavLink
        to={AppRoutes.WeeklyGames}
        hidden={!isLoggedIn}
        className={isActionButtonsDisabled ? styles.NavLinkDisabled : ''}
        aria-disabled={isActionButtonsDisabled}
      >
        <ShowWeeklyGamesButton
          disabled={isActionButtonsDisabled}
          selected={selectedScene === AppScene.WeeklyGames}
          icon={ScheduleIcon}
          iconSize={btnSize === ComponentSize.Large ? 30 : 22}
          title={titleWeeklyGames}
          size={btnSize}
          onIconButtonTap={() => setShowWeeklyGamesPage(selectedScene !== AppScene.WeeklyGames)}
        />
      </NavLink>
      <NavLink
        to={AppRoutes.Standings}
        hidden={!isLoggedIn}
        className={isActionButtonsDisabled ? styles.NavLinkDisabled : ''}
        aria-disabled={isActionButtonsDisabled}
      >
        <ShowStandingsButton
          disabled={isActionButtonsDisabled}
          selected={selectedScene === AppScene.Standings}
          icon={StandingsIcon}
          title={titleShowStandings}
          iconSize={btnSize === ComponentSize.Large ? 30 : 22}
          size={btnSize}
          onIconButtonTap={() => setStandingsActive(selectedScene !== AppScene.Standings)}
        />
      </NavLink>
      <NavLink
        to={AppRoutes.TeamStats}
        hidden={!isLoggedIn}
        className={isActionButtonsDisabled ? styles.NavLinkDisabled : ''}
        aria-disabled={isActionButtonsDisabled}
      >
        <ShowStatsButton
          disabled={isActionButtonsDisabled}
          selected={selectedScene === AppScene.Teams}
          icon={FinanceIcon}
          title={titleShowStats}
          iconSize={btnSize === ComponentSize.Large ? 30 : 22}
          size={btnSize}
          onIconButtonTap={() => setShowStatsForAllTeams(selectedScene !== AppScene.Teams)}
        />
      </NavLink>

      <NavLink
        to={AppRoutes.CategoryRankings}
        hidden={!isLoggedIn}
        className={isActionButtonsDisabled ? styles.NavLinkDisabled : ''}
        aria-disabled={isActionButtonsDisabled}
      >
        <ShowStatsButton
          disabled={isActionButtonsDisabled}
          selected={selectedScene === AppScene.CategoryRankings}
          icon={FinanceModeIcon}
          title={titleShowStats}
          iconSize={btnSize === ComponentSize.Large ? 30 : 22}
          size={btnSize}
          onIconButtonTap={() => setShowCategoryRankings(selectedScene !== AppScene.CategoryRankings)}
        />
      </NavLink>

      <NavLink
        to={AppRoutes.HeadToHead}
        hidden={!isLoggedIn}
        className={isActionButtonsDisabled ? styles.NavLinkDisabled : ''}
        aria-disabled={isActionButtonsDisabled}
      >
        <ShowHeadToHeadButton
          disabled={isActionButtonsDisabled}
          selected={selectedScene === AppScene.HeadToHead}
          icon={HeadToHeadIcon}
          title="Head To Head"
          iconSize={btnSize === ComponentSize.Large ? 30 : 22}
          size={btnSize}
          onIconButtonTap={() => setShowHeadToHead(selectedScene !== AppScene.HeadToHead)}
        />
      </NavLink>

      <NavLink
        to={AppRoutes.Comparison}
        hidden={!isLoggedIn}
        className={isActionButtonsDisabled ? styles.NavLinkDisabled : ''}
        aria-disabled={isActionButtonsDisabled}
      >
        <ShowStatsButton
          disabled={isActionButtonsDisabled}
          selected={selectedScene === AppScene.Comparison}
          icon={SwordsIcon}
          title="Compare Teams"
          iconSize={btnSize === ComponentSize.Large ? 30 : 22}
          size={btnSize}
          onIconButtonTap={() => setShowComparison(selectedScene !== AppScene.Comparison)}
        />
      </NavLink>

      <UserAccountButton
        hidden={!isLoggedIn}
        disabled={!isLoggedIn}
        className={styles.MarginLeft32}
        selected={selectedScene === AppScene.Landing}
        icon={UserSettingsIcon}
        title={titleUserAccount}
        iconSize={btnSize === ComponentSize.Large ? 30 : 22}
        size={btnSize}
        onIconButtonTap={showUserAccountPanel}
      />

      {!isLoggedIn && (
        <ASButton
          title="titleLoginRegister"
          disabled={isLoggedInButtonDisabled}
          onTapped={() => navigate(AppRoutes.Auth)}
        >
          {titleLoginRegister}
        </ASButton>
      )}
    </div>
  );
};

export default TopNavigation;
